import { Link, Outlet, useLocation } from 'react-router-dom'

const Newscan = () => {
  const location = useLocation()
  const activePath = location.pathname.split('/').pop()

  const tabs = ['endpoints', 'detectors', 'methods', 'review']

  return (
    <div className="text-white min-h-screen">
      <div className="px-8 pt-6">
        <h1 className="text-4xl font-bold mb-12 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          New Scan
        </h1>

        {/* Enhanced Progress Bar Container */}
        <div className="mb-12 max-w-5xl mx-auto">
          <div className="relative">
            {/* Background Progress Line - positioned to connect circle centers */}
            <div className="absolute top-7 h-0.5 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800"
                 style={{
                   left: '20px', // Half of circle width (40px/2)
                   right: '20px', // Half of circle width (40px/2)
                 }}></div>
            
            {/* Active Progress Line - positioned to connect circle centers */}
            <div 
              className="absolute top-7 h-0.5 bg-gradient-to-r from-blue-500 via-blue-400 to-cyan-400 transition-all duration-1000 ease-out shadow-sm shadow-blue-500/50"
              style={{
                left: '20px', // Half of circle width (40px/2)
                right: '20px', // Half of circle width (40px/2)
                width: `${(tabs.findIndex(t => t === activePath) / (tabs.length - 1)) * (100 - 8)}%`, // Adjusted width calculation
                transformOrigin: 'left'
              }}
            ></div>
            
            {/* Progress Steps */}
            <div className="relative flex justify-between items-center">
              {tabs.map((tab, index) => {
                const isActive = tab === activePath
                const isCompleted = index < tabs.findIndex(t => t === activePath)

                
                return (
                  <div key={tab} className="flex flex-col items-center group">
                    <Link
                      to={`/newscan/${tab}`}
                      className="flex flex-col items-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-lg p-2"
                    >
                      {/* Enhanced Step Circle */}
                      <div className="relative flex items-center justify-center">
                        {/* Outer glow ring for active step - properly centered */}
                        {isActive && (
                          <div className="absolute w-12 h-12 rounded-full bg-blue-500/20 scale-125 -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"></div>
                        )}
                        
                        {/* Main Circle */}
                        <div className={`
                          relative w-10 h-10 rounded-full border-2 flex items-center justify-center
                          transition-all duration-500 ease-out transform
                          ${isCompleted 
                            ? 'bg-gradient-to-br from-green-500 to-emerald-600 border-green-400 shadow-lg shadow-green-500/30 scale-105' 
                            : isActive 
                              ? 'bg-gradient-to-br from-blue-600 to-blue-700 border-blue-400 shadow-xl shadow-blue-500/40 scale-110' 
                              : 'bg-gradient-to-br from-gray-700 to-gray-800 border-gray-600 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/20 hover:scale-105'
                          }
                          group-hover:transform group-hover:scale-110
                        `}>
                          
                          {/* Step Content */}
                          {isCompleted ? (
                            <div className="relative">
                              <svg 
                                className="w-5 h-5 text-white" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  strokeWidth={3} 
                                  d="M5 13l4 4L19 7" 
                                />
                              </svg>
                            </div>
                          ) : (
                            <span className={`
                              text-sm font-bold transition-all duration-300
                              ${isActive ? 'text-white' : 'text-gray-300 group-hover:text-white'}
                            `}>
                              {index + 1}
                            </span>
                          )}
                          
                          {/* Inner highlight */}
                          <div className={`
                            absolute inset-1 rounded-full transition-opacity duration-300
                            ${isActive ? 'bg-white/10' : 'opacity-0'}
                          `}></div>
                        </div>
                        
                        {/* Completion animation ring - properly centered */}
                        {isCompleted && (
                          <div className="absolute w-10 h-10 rounded-full border-2 border-green-400 opacity-20 -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"></div>
                        )}
                      </div>
                      
                      {/* Enhanced Label */}
                      <div className="mt-4 text-center">
                        <div className={`
                          text-sm font-semibold transition-all duration-300 transform
                          ${isActive 
                            ? 'text-blue-400 scale-105 font-bold' 
                            : isCompleted 
                              ? 'text-emerald-400' 
                              : 'text-gray-400 group-hover:text-gray-200 group-hover:scale-105'
                          }
                        `}>
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </div>
                        
                        {/* Status indicator */}
                        <div className={`
                          mt-1 text-xs transition-all duration-300
                          ${isActive 
                            ? 'text-blue-300 opacity-100' 
                            : isCompleted 
                              ? 'text-emerald-300 opacity-100' 
                              : 'text-transparent opacity-0 group-hover:opacity-50 group-hover:text-gray-500'
                          }
                        `}>
                          {isActive ? 'In Progress' : isCompleted ? 'Completed' : 'Pending'}
                        </div>
                      </div>
                    </Link>
                  </div>
                )
              })}
            </div>
            
            {/* Progress Percentage Indicator */}
            <div className="mt-6 text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-800/50 border border-gray-700">
                <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                <span className="text-sm text-gray-300">
                  Step {tabs.findIndex(t => t === activePath) + 1} of {tabs.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className="px-8">
        <Outlet />
      </div>
    </div>
  )
}

export default Newscan